import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface RolePermissions {
  canViewSystemPolicies: boolean;
  canViewCompanyPolicies: boolean;
  canViewUserPolicies: boolean;
  canCreateSystemPolicies: boolean;
  canCreateCompanyPolicies: boolean;
  canCreateUserPolicies: boolean;
  canEditSystemPolicies: boolean;
  canEditCompanyPolicies: boolean;
  canEditUserPolicies: boolean;
  canDeleteSystemPolicies: boolean;
  canDeleteCompanyPolicies: boolean;
  canDeleteUserPolicies: boolean;
  canTemplateSystemPolicies: boolean;
  canTemplateCompanyPolicies: boolean;
  canTemplateUserPolicies: boolean;
  canManageUsers: boolean;
  canManageCompanies: boolean;
  canViewAllLogs: boolean;
  canViewCompanyLogs: boolean;
  canViewOwnLogs: boolean;
}

export const useRolePermissions = (): RolePermissions => {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) {
    return {
      canViewSystemPolicies: false,
      canViewCompanyPolicies: false,
      canViewUserPolicies: false,
      canCreateSystemPolicies: false,
      canCreateCompanyPolicies: false,
      canCreateUserPolicies: false,
      canEditSystemPolicies: false,
      canEditCompanyPolicies: false,
      canEditUserPolicies: false,
      canDeleteSystemPolicies: false,
      canDeleteCompanyPolicies: false,
      canDeleteUserPolicies: false,
      canTemplateSystemPolicies: false,
      canTemplateCompanyPolicies: false,
      canTemplateUserPolicies: false,
      canManageUsers: false,
      canManageCompanies: false,
      canViewAllLogs: false,
      canViewCompanyLogs: false,
      canViewOwnLogs: false,
    };
  }

  const role = user.role;

  // System Admin - can do everything
  if (role === 'sysadmin') {
    return {
      canViewSystemPolicies: true,
      canViewCompanyPolicies: true,
      canViewUserPolicies: true,
      canCreateSystemPolicies: true,
      canCreateCompanyPolicies: true,
      canCreateUserPolicies: true,
      canEditSystemPolicies: true,
      canEditCompanyPolicies: true,
      canEditUserPolicies: true,
      canDeleteSystemPolicies: true,
      canDeleteCompanyPolicies: true,
      canDeleteUserPolicies: true,
      canTemplateSystemPolicies: true,
      canTemplateCompanyPolicies: true,
      canTemplateUserPolicies: true,
      canManageUsers: true,
      canManageCompanies: true,
      canViewAllLogs: true,
      canViewCompanyLogs: true,
      canViewOwnLogs: true,
    };
  }

  // Company Admin - can manage company and user policies
  if (role === 'admin') {
    return {
      canViewSystemPolicies: true,
      canViewCompanyPolicies: true,
      canViewUserPolicies: true,
      canCreateSystemPolicies: false,
      canCreateCompanyPolicies: true,
      canCreateUserPolicies: true,
      canEditSystemPolicies: false,
      canEditCompanyPolicies: true,
      canEditUserPolicies: true,
      canDeleteSystemPolicies: false,
      canDeleteCompanyPolicies: true,
      canDeleteUserPolicies: true,
      canTemplateSystemPolicies: true,
      canTemplateCompanyPolicies: true,
      canTemplateUserPolicies: true,
      canManageUsers: false,
      canManageCompanies: false,
      canViewAllLogs: false,
      canViewCompanyLogs: true,
      canViewOwnLogs: true,
    };
  }

  // Regular User - can only manage their own policies
  if (role === 'user') {
    return {
      canViewSystemPolicies: true,
      canViewCompanyPolicies: true,
      canViewUserPolicies: true,
      canCreateSystemPolicies: false,
      canCreateCompanyPolicies: false,
      canCreateUserPolicies: true,
      canEditSystemPolicies: false,
      canEditCompanyPolicies: false,
      canEditUserPolicies: true,
      canDeleteSystemPolicies: false,
      canDeleteCompanyPolicies: false,
      canDeleteUserPolicies: true,
      canTemplateSystemPolicies: true,
      canTemplateCompanyPolicies: true,
      canTemplateUserPolicies: true,
      canManageUsers: false,
      canManageCompanies: false,
      canViewAllLogs: false,
      canViewCompanyLogs: false,
      canViewOwnLogs: true,
    };
  }

  // Default - no permissions (e.g. translator_xx)
  return {
    canViewSystemPolicies: false,
    canViewCompanyPolicies: false,
    canViewUserPolicies: false,
    canCreateSystemPolicies: false,
    canCreateCompanyPolicies: false,
    canCreateUserPolicies: false,
    canEditSystemPolicies: false,
    canEditCompanyPolicies: false,
    canEditUserPolicies: false,
    canDeleteSystemPolicies: false,
    canDeleteCompanyPolicies: false,
    canDeleteUserPolicies: false,
    canTemplateSystemPolicies: false,
    canTemplateCompanyPolicies: false,
    canTemplateUserPolicies: false,
    canManageUsers: false,
    canManageCompanies: false,
    canViewAllLogs: false,
    canViewCompanyLogs: false,
    canViewOwnLogs: true,
  };
  }, [user]);
};
